<script lang="ts" setup>
import { sanitize } from 'isomorphic-dompurify'
import { marked } from 'marked'

interface VRenderMarkdownProps {
  content: string
  tag?: 'div' | 'p' | 'li'
  placeholder?: Record<string, number | undefined> // e.g. { amountOfAvailableCooks: 5 }
}

const props = withDefaults(defineProps<VRenderMarkdownProps>(), {
  tag: 'div',
  placeholder: undefined
})

const sanitizedHtml = computed(() => {
  let contentWithPlaceholder = props.content
  if (!contentWithPlaceholder) {
    return ''
  }

  if (props.placeholder) {
    Object.entries(props.placeholder).forEach(([placeholderKey, placeholderData]) => {
      if (!placeholderData) {
        const regexPattern = new RegExp(`.*\\{${placeholderKey}\\}.*\\n?`, 'g')
        contentWithPlaceholder = contentWithPlaceholder.replace(regexPattern, '')
      } else {
        contentWithPlaceholder = contentWithPlaceholder.replace(
          `{${placeholderKey}}`,
          String(placeholderData)
        )
      }
    })
  }

  // Use `parseInline` to avoid wrapping the content in a paragraph tag
  const html =
    props.tag === 'p' || props.tag === 'li'
      ? marked.parseInline(contentWithPlaceholder)
      : marked.parse(contentWithPlaceholder)
  return sanitize(html as string, {
    ALLOWED_ATTR: ['href', 'target', 'rel', 'download', 'title']
  })
})
</script>

<template>
  <!-- List every tag explicitly due to Vue bug in SSR: https://github.com/vuejs/core/issues/12520 -->
  <!--eslint-disable vue/no-v-text-v-html-on-component -->
  <div v-if="tag === 'div'" v-html="sanitizedHtml" />
  <p v-else-if="tag === 'p'" v-html="sanitizedHtml" />
  <li v-else-if="tag === 'li'" v-html="sanitizedHtml" />
  <!--eslint-enable-->
</template>

<style scoped></style>
